/**
 * Fiche utilisateur : identité, rôles, historique administratif (docs/16 §2).
 *
 * Chaque action passe par une confirmation à motif obligatoire. Les boutons
 * sont masqués selon les permissions — pour éviter un 403 prévisible, jamais
 * pour protéger : c'est le serveur qui refuse (docs/07 §8).
 */
import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useAdminRoles,
  useAdminUser,
  useGrantRole,
  useReactivateUser,
  useRevokeRole,
  useSuspendUser,
} from "@/features/admin/api/useAdmin";
import { ReasonDialog } from "@/features/admin/components/ReasonDialog";
import { StatusBadge } from "@/features/admin/components/StatusBadge";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { usePermissions } from "@/features/auth";
import { UserBillingCard } from "@/features/billing";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { asDocument } from "@/shared/richtext/document";
import { RichContent } from "@/shared/richtext/RichContent";
import { Card, DefinitionList } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

type Dialog = "suspend" | "reactivate" | "grant" | "revoke";

export function AdminUserDetailPage() {
  const { t, i18n } = useTranslation("admin");
  const { userId } = useParams({ from: "/admin/admin/users/$userId" });
  const { can } = usePermissions();
  const errorMessage = useApiErrorMessage();

  const user = useAdminUser(userId);
  const roles = useAdminRoles();
  const suspend = useSuspendUser();
  const reactivate = useReactivateUser();
  const grant = useGrantRole();
  const revoke = useRevokeRole();

  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [selectedRole, setSelectedRole] = useState("");

  const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value),
    );

  const close = () => {
    setDialog(null);
  };

  if (user.isPending) {
    return <Skeleton className="h-4 w-2/3" label={t("detail.loading")} />;
  }
  if (user.isError) {
    return <ErrorState error={user.error} onRetry={() => void user.refetch()} />;
  }

  const account = user.data;
  const assignable = (roles.data ?? []).filter((role) => !account.roles.includes(role.slug));
  const { profile, settings } = account;
  // `null` et non `undefined` : le champ est toujours envoyé (schéma OpenAPI
  // obligatoire), et c'est ce qui rend cette comparaison fiable — un champ
  // facultatif serait `undefined` sur tout compte et le bandeau s'afficherait
  // partout (HANDOFF §2).
  const scheduledDeletion = account.deletion_scheduled_for;
  const profileIsBlank =
    profile.headline === null &&
    profile.bio === null &&
    profile.website_url === null &&
    profile.subjects.length === 0 &&
    profile.languages.length === 0;

  const consent = (given: boolean) => (given ? t("detail.granted") : t("detail.refused"));

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/users" className="text-brand-600 text-sm hover:underline">
          ← {t("detail.back")}
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">{account.display_name}</h1>
          <StatusBadge status={account.status} />
        </div>
      </div>

      {scheduledDeletion !== null && (
        <Alert tone="warning">
          <p className="font-medium">
            {t("detail.deletionScheduled", { date: formatDateTime(scheduledDeletion) })}
          </p>
          {account.deletion_requested_at !== null && (
            <p className="mt-1">
              {t("detail.deletionRequestedAt", {
                date: formatDateTime(account.deletion_requested_at),
              })}
            </p>
          )}
        </Alert>
      )}

      <Card title={t("detail.identity")}>
        <DefinitionList
          items={[
            { label: "Email", value: account.email },
            {
              label: t("detail.emailVerified"),
              value: account.is_email_verified
                ? t("detail.emailVerified")
                : t("detail.emailUnverified"),
            },
            { label: t("users.columns.createdAt"), value: formatDateTime(account.created_at) },
            {
              label: t("users.columns.lastLogin"),
              value:
                account.last_login_at === null
                  ? t("users.never")
                  : formatDateTime(account.last_login_at),
            },
          ]}
        />
      </Card>

      <Card title={t("detail.profile")}>
        {profileIsBlank ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("detail.profileEmpty")}</p>
        ) : (
          <DefinitionList
            items={[
              { label: t("detail.headline"), value: profile.headline ?? "—" },
              { label: t("detail.website"), value: profile.website_url ?? "—" },
              { label: t("detail.subjects"), value: profile.subjects.join(", ") || "—" },
              { label: t("detail.languages"), value: profile.languages.join(", ") || "—" },
              {
                label: t("detail.bio"),
                // `DefinitionList` prend un `ReactNode` : la biographie est un
                // document depuis qu'elle est de la prose riche, et l'afficher
                // en JSON brut serait la seule façon de se tromper ici.
                value:
                  profile.bio === null ? "—" : <RichContent document={asDocument(profile.bio)} />,
              },
              {
                label: t("detail.profileVerified"),
                value: profile.is_verified
                  ? t("detail.profileVerified")
                  : t("detail.profileUnverified"),
              },
            ]}
          />
        )}
      </Card>

      <Card title={t("detail.preferences")}>
        <DefinitionList
          items={[
            {
              label: t("detail.shareProgress"),
              value: consent(settings.share_progress_with_teachers),
            },
            { label: t("detail.marketingEmails"), value: consent(settings.marketing_emails) },
            { label: t("detail.theme"), value: settings.theme },
          ]}
        />
        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
          {t("detail.preferencesHelp")}
        </p>
      </Card>

      <Card title={t("detail.roles")}>
        <p className="text-sm">{account.roles.join(", ") || "—"}</p>
        {can("admin.role.manage") && (
          <div className="mt-4 flex flex-wrap items-end gap-2">
            <div className="space-y-1.5">
              <label htmlFor="role-select" className="block text-sm font-medium">
                {t("detail.rolePlaceholder")}
              </label>
              <select
                id="role-select"
                value={selectedRole}
                onChange={(event) => {
                  setSelectedRole(event.target.value);
                }}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
              >
                <option value="">—</option>
                {assignable.map((role) => (
                  <option key={role.slug} value={role.slug}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="secondary"
              disabled={selectedRole === ""}
              onClick={() => {
                setDialog("grant");
              }}
            >
              {t("detail.grantRole")}
            </Button>
            {account.roles.length > 0 && (
              <Button
                variant="ghost"
                onClick={() => {
                  setSelectedRole(account.roles[0] ?? "");
                  setDialog("revoke");
                }}
              >
                {t("detail.revokeRole")}
              </Button>
            )}
          </div>
        )}

        {dialog === "grant" && (
          <ReasonDialog
            title={t("actions.grantRoleTitle")}
            warning={t("actions.roleWarning")}
            confirmLabel={t("actions.confirm")}
            isPending={grant.isPending}
            error={grant.isError ? errorMessage(grant.error) : undefined}
            onCancel={close}
            onConfirm={(reason) => {
              grant.mutate({ userId, role: selectedRole, reason }, { onSuccess: close });
            }}
          />
        )}

        {dialog === "revoke" && (
          <ReasonDialog
            title={t("actions.revokeRoleTitle")}
            warning={t("actions.roleWarning")}
            confirmLabel={t("actions.confirm")}
            isPending={revoke.isPending}
            error={revoke.isError ? errorMessage(revoke.error) : undefined}
            onCancel={close}
            onConfirm={(reason) => {
              revoke.mutate({ userId, role: selectedRole, reason }, { onSuccess: close });
            }}
          />
        )}
      </Card>

      {can("admin.user.suspend") && (
        <Card>
          {dialog === null && (
            <div className="flex gap-2">
              {account.status === "suspended" ? (
                <Button
                  onClick={() => {
                    setDialog("reactivate");
                  }}
                >
                  {t("actions.reactivate")}
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    setDialog("suspend");
                  }}
                >
                  {t("actions.suspend")}
                </Button>
              )}
            </div>
          )}

          {dialog === "suspend" && (
            <ReasonDialog
              title={t("actions.suspendTitle")}
              warning={t("actions.suspendWarning")}
              confirmLabel={t("actions.confirm")}
              isPending={suspend.isPending}
              error={suspend.isError ? errorMessage(suspend.error) : undefined}
              onCancel={close}
              onConfirm={(reason) => {
                suspend.mutate({ userId, reason }, { onSuccess: close });
              }}
            />
          )}

          {dialog === "reactivate" && (
            <ReasonDialog
              title={t("actions.reactivateTitle")}
              warning={t("actions.reactivateWarning")}
              confirmLabel={t("actions.confirm")}
              isPending={reactivate.isPending}
              error={reactivate.isError ? errorMessage(reactivate.error) : undefined}
              onCancel={close}
              onConfirm={(reason) => {
                reactivate.mutate({ userId, reason }, { onSuccess: close });
              }}
            />
          )}
        </Card>
      )}

      {/* L'offre, entre les rôles et l'activité : c'est le troisième levier
          d'un administrateur sur un compte, après ce qu'il peut faire (rôles)
          et ce qu'il a fait (journal). La carte se masque d'elle-même sans
          `billing.subscription.manage_any`. */}
      <UserBillingCard userId={userId} />

      <Card title={t("detail.activity")}>
        {account.recent_activity.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("detail.noActivity")}</p>
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {account.recent_activity.map((entry) => (
              <li key={entry.id} className="py-3">
                <p className="text-sm font-medium">
                  {t(`actionNames.${entry.action}`, { defaultValue: entry.action })}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {formatDateTime(entry.created_at)}
                  {typeof entry.changes?.["reason"] === "string" && ` · ${entry.changes["reason"]}`}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
